/**
 * Minimal JPEG EXIF/APPn metadata stripper.
 *
 * Marketplaces do not need camera metadata, and EXIF can carry orientation
 * flags that would silently rotate a listing image. AlgorithmShield re-encodes
 * every image through an HTML5 Canvas (which already drops metadata), then runs
 * this byte-level pass as an explicit, testable guarantee before zipping.
 *
 * Structure of a JPEG:
 *   FFD8                          SOI (start of image)
 *   FFxx <len> <payload> ...      marker segments (APP0/APP1/COM/DQT/...)
 *   FFDA <len> <payload> ...      SOS (start of scan) - entropy data until FFD9
 *   FFD9                          EOI (end of image)
 *
 * Segment layout: [FF][marker][lenHi][lenLo][payload ...]
 * `len` covers the 2 length bytes themselves, so a segment ends at
 * `ffIndex + 2 + len`.
 */

const SOI = 0xd8;
const EOI = 0xd9;
const SOS = 0xda;
const APP1 = 0xe1;
const APP13 = 0xed;
const COM = 0xfe;
const TEM = 0x01;
const RST_MIN = 0xd0;
const RST_MAX = 0xd7;

/** APP1 segments beginning with "Exif\0\0" are EXIF payloads and get dropped. */
const EXIF_MAGIC = [0x45, 0x78, 0x69, 0x66, 0x00, 0x00];

function startsWithExifMagic(bytes: Uint8Array, offset: number): boolean {
  if (offset + EXIF_MAGIC.length > bytes.length) return false;
  for (let i = 0; i < EXIF_MAGIC.length; i++) {
    if (bytes[offset + i] !== EXIF_MAGIC[i]) return false;
  }
  return true;
}

function isStandaloneMarker(marker: number): boolean {
  return marker === SOI || marker === EOI || marker === TEM || (marker >= RST_MIN && marker <= RST_MAX);
}

/**
 * Removes EXIF (APP1), IPTC (APP13) and COM metadata from a JPEG byte stream.
 * Returns a new Uint8Array; non-JPEG input is returned as an untouched copy.
 */
export function stripExif(jpeg: Uint8Array): Uint8Array {
  // Not a JPEG (SOI marker)? Nothing to strip.
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== SOI) {
    return new Uint8Array(jpeg);
  }

  const out: number[] = [0xff, SOI];
  let pos = 2;

  while (pos + 1 < jpeg.length) {
    // Preserve stray (non-marker) bytes verbatim.
    if (jpeg[pos] !== 0xff) {
      out.push(jpeg[pos]);
      pos++;
      continue;
    }

    // Collapse repeated 0xFF padding; `pos` ends on the last 0xFF.
    let p = pos;
    while (p + 1 < jpeg.length && jpeg[p + 1] === 0xff) p++;

    const marker = jpeg[p + 1];
    if (marker === undefined) break;

    // Standalone markers carry no length field.
    if (isStandaloneMarker(marker)) {
      out.push(0xff, marker);
      pos = p + 2;
      if (marker === EOI) break;
      continue;
    }

    // Entropy-coded data starts at SOS: copy the remainder untouched.
    if (marker === SOS) {
      for (let i = p; i < jpeg.length; i++) out.push(jpeg[i]);
      break;
    }

    const lengthHi = jpeg[p + 2];
    const lengthLo = jpeg[p + 3];
    if (lengthHi === undefined || lengthLo === undefined) break;
    const segmentLength = (lengthHi << 8) | lengthLo;
    if (segmentLength < 2) break;

    const payloadStart = p + 4;
    const segmentEnd = p + 2 + segmentLength;
    if (segmentEnd > jpeg.length) break;

    const isExif = marker === APP1 && startsWithExifMagic(jpeg, payloadStart);
    const isMetadata = marker === APP13 || marker === COM;

    if (!isExif && !isMetadata) {
      for (let i = p; i < segmentEnd; i++) out.push(jpeg[i]);
    }

    pos = segmentEnd;
  }

  return Uint8Array.from(out);
}

/** True when the byte stream still contains an EXIF APP1 segment. */
export function hasExif(jpeg: Uint8Array): boolean {
  if (jpeg.length < 4 || jpeg[0] !== 0xff || jpeg[1] !== SOI) return false;

  let pos = 2;
  while (pos + 1 < jpeg.length) {
    if (jpeg[pos] !== 0xff) {
      pos++;
      continue;
    }
    const marker = jpeg[pos + 1];
    if (marker === undefined) return false;
    if (marker === 0xff) {
      pos++;
      continue;
    }
    if (marker === SOS) return false;
    if (isStandaloneMarker(marker)) {
      pos += 2;
      continue;
    }
    const lengthHi = jpeg[pos + 2];
    const lengthLo = jpeg[pos + 3];
    if (lengthHi === undefined || lengthLo === undefined) return false;
    const segmentLength = (lengthHi << 8) | lengthLo;
    if (marker === APP1 && startsWithExifMagic(jpeg, pos + 4)) return true;
    pos += 2 + segmentLength;
  }
  return false;
}
